import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
  updateServicesPedido,
} from "../services/firebaseService";
import { Team, Invoice, DateRange } from "../types";
import "./CoelbaReport.css";

interface CoelbaServiceData {
  rowId: string;
  planilha: string;
  codigo: string;
  pedido: string;
  descricaoServico: string;
  itensContrato: string;
  unidade: string;
  quantidade: number;
  preco: number;
  fator: number;
  total: number;
  si: string;
  municipio: string;
  selected: boolean;
  serviceUuids: Array<{
    invoiceKey: string;
    serviceUuid: string;
    teamId: string;
  }>;
}

const CoelbaReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "planilha" | "codigo" | "total" | "municipio"
  >("planilha");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set(),
  );
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      // Start with no teams selected - user must choose them manually
      setSelectedTeams([]);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
    );
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
    );

    let startDate, endDate;

    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadReportDataWithTeams = async (teamsToLoad: string[]) => {
    if (
      teamsToLoad.length === 0 ||
      !dateRange.startDate ||
      !dateRange.endDate
    ) {
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];

      for (const teamId of teamsToLoad) {
        const teamInvoices = await getInvoicesForReport(
          teamId,
          dateRange.startDate,
          dateRange.endDate,
        );
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = async () => {
    if (
      selectedTeams.length === 0 ||
      !dateRange.startDate ||
      !dateRange.endDate
    ) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }

    await loadReportDataWithTeams(selectedTeams);
  };

  // Helper function to create deterministic UUID from invoice key and service index
  const createServiceUuid = (
    invoiceKey: string,
    serviceIndex: number,
  ): string => {
    return `${invoiceKey}-service-${serviceIndex}`;
  };

  const coelbaServiceData = useMemo(() => {
    const serviceMap = new Map<string, CoelbaServiceData>();

    invoices.forEach((invoice) => {
      if (invoice.services && Array.isArray(invoice.services)) {
        invoice.services.forEach((service, serviceIndex) => {
          if (service && service.code) {
            const quantidade = parseFloat(service.quantity?.toString() || "0");
            const preco = parseFloat(service.price?.toString() || "0");
            const fator = parseFloat(service.factor?.toString() || "1");
            const total = quantidade * preco * fator;
            const serviceKey = `${invoice.city}-${service.code}`;

            // Create deterministic UUID based on invoice key and service index
            const serviceUuid = createServiceUuid(
              invoice.key || "",
              serviceIndex,
            );

            if (serviceMap.has(serviceKey)) {
              // Aggregate existing service
              const existing = serviceMap.get(serviceKey)!;
              existing.quantidade += quantidade;
              existing.total += total;
              existing.planilha += `,<br/>${invoice.id}`;
              existing.serviceUuids.push({
                invoiceKey: invoice.key || "",
                serviceUuid: serviceUuid,
                teamId: invoice.team || "",
              });
            } else {
              // Create new service entry
              serviceMap.set(serviceKey, {
                rowId: `${Date.now().toString(36)}-${Math.random().toString(36).substr(2)}`,
                planilha: invoice.id || "",
                codigo: service.code,
                pedido: "-", // Will be calculated after aggregation
                descricaoServico: service.description || service.activity || "",
                itensContrato: "4600035150",
                unidade: service.unid || "UD",
                quantidade: quantidade,
                preco: preco,
                fator: fator,
                total: total,
                si: invoice.pms || "",
                municipio: invoice.city || "",
                selected: false,
                serviceUuids: [
                  {
                    invoiceKey: invoice.key || "",
                    serviceUuid: serviceUuid,
                    teamId: invoice.team || "",
                  },
                ],
              });
            }
          }
        });
      }
    });

    // After aggregation, calculate pedido status for each aggregated service
    const result = Array.from(serviceMap.values()).map((item) => {
      // Check all services in this aggregated group to determine pedido status
      const pedidos = new Set<string>();

      item.serviceUuids.forEach((serviceRef) => {
        const invoice = invoices.find(
          (inv) => inv.key === serviceRef.invoiceKey,
        );
        if (invoice && invoice.services) {
          // Extract service index from deterministic UUID
          const serviceIndex = parseInt(
            serviceRef.serviceUuid.split("-service-")[1],
          );
          const service = invoice.services[serviceIndex];
          console.log(
            `Checking service index ${serviceIndex} in invoice ${invoice.id}:`,
            service?.pedido,
          );
          if (service && service.pedido && service.pedido !== "-") {
            pedidos.add(service.pedido);
            console.log(`Added pedido ${service.pedido} to set`);
          }
        }
      });

      // If any service has a pedido, use the first one found, otherwise use '-'
      item.pedido = pedidos.size > 0 ? Array.from(pedidos)[0] : "-";
      console.log(`Final pedido for ${item.codigo}:`, item.pedido);

      return item;
    });

    return result;
  }, [invoices]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = coelbaServiceData.filter(
      (item) =>
        searchTerm === "" ||
        item.planilha.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.descricaoServico
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.municipio.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.si.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "planilha":
          aValue = a.planilha;
          bValue = b.planilha;
          break;
        case "codigo":
          aValue = a.codigo;
          bValue = b.codigo;
          break;
        case "total":
          aValue = a.total;
          bValue = b.total;
          break;
        case "municipio":
          aValue = a.municipio;
          bValue = b.municipio;
          break;
        default:
          aValue = a.planilha;
          bValue = b.planilha;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  }, [coelbaServiceData, searchTerm, sortBy, sortOrder]);

  const reportSummary = useMemo(() => {
    const totalServices = filteredAndSortedData.length;
    const totalValue = filteredAndSortedData.reduce(
      (sum, item) => sum + item.total,
      0,
    );
    const totalValueWithPedido = filteredAndSortedData
      .filter((item) => item.pedido !== "-" && item.pedido !== "")
      .reduce((sum, item) => sum + item.total, 0);
    const totalValueWithoutPedido = totalValue - totalValueWithPedido;
    const totalQuantity = filteredAndSortedData.reduce(
      (sum, item) => sum + item.quantidade,
      0,
    );
    const uniqueCodes = new Set(
      filteredAndSortedData.map((item) => item.codigo),
    ).size;
    const uniqueMunicipios = new Set(
      filteredAndSortedData.map((item) => item.municipio),
    ).size;
    const selectedCount = Array.from(selectedServices).length;

    return {
      totalServices,
      totalValue,
      totalValueWithPedido,
      totalValueWithoutPedido,
      totalQuantity,
      uniqueCodes,
      uniqueMunicipios,
      selectedCount,
    };
  }, [filteredAndSortedData, selectedServices]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId],
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map((team) => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const handleSort = (
    column: "planilha" | "codigo" | "total" | "municipio",
  ) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleServiceSelection = (serviceId: string) => {
    setSelectedServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const handleSelectAllServices = () => {
    const allServiceIds = filteredAndSortedData.map((_, index) =>
      index.toString(),
    );
    setSelectedServices(new Set(allServiceIds));
  };

  const handleDeselectAllServices = () => {
    setSelectedServices(new Set());
  };

  const handleUpdatePedidos = async () => {
    const selectedItems = filteredAndSortedData.filter((_, index) =>
      selectedServices.has(index.toString()),
    );

    if (selectedItems.length === 0) {
      alert("Selecione pelo menos um item para atualizar.");
      return;
    }

    const confirmed = window.confirm(
      "Deseja dar baixa nos códigos selecionados?",
    );
    if (!confirmed) return;

    const pedido = window.prompt(
      "Entre o número do pedido. Deixe em branco para apagar o pedido.",
    );
    if (pedido === null) return; // User cancelled

    setLoading(true);
    try {
      const updates: { [key: string]: any } = {};

      // Process each selected item
      for (const item of selectedItems) {
        console.log(
          "Processing item:",
          item.codigo,
          "with",
          item.serviceUuids.length,
          "service UUIDs",
        );

        // Process each service UUID associated with this aggregated row
        for (const serviceRef of item.serviceUuids) {
          console.log("Processing serviceRef:", serviceRef);

          // Find the original invoice to get its services
          const invoice = invoices.find(
            (inv) => inv.key === serviceRef.invoiceKey,
          );
          console.log(
            "Found invoice:",
            invoice?.id,
            "with",
            invoice?.services?.length,
            "services",
          );

          if (invoice && invoice.services) {
            const updatedServices = [...(invoice.services || [])];

            // Extract service index from deterministic UUID
            const serviceIndex = parseInt(
              serviceRef.serviceUuid.split("-service-")[1],
            );
            console.log(
              "Updating service at index",
              serviceIndex,
              "with pedido:",
              pedido,
            );

            if (updatedServices[serviceIndex]) {
              // Update the specific service by index
              if (pedido.trim() !== "") {
                updatedServices[serviceIndex] = {
                  ...updatedServices[serviceIndex],
                  pedido: pedido,
                  paid: true,
                };
              } else {
                updatedServices[serviceIndex] = {
                  ...updatedServices[serviceIndex],
                  pedido: "-",
                  paid: false,
                };
              }

              // Add to updates batch
              const teamId = serviceRef.teamId || selectedTeams[0]; // Fallback to first selected team
              const updatePath = `/forms/${teamId}/${serviceRef.invoiceKey}/services`;
              console.log("Adding to updates:", updatePath);
              updates[updatePath] = updatedServices;
            }
          }
        }
      }

      // Execute batch update
      await updateServicesPedido(updates);

      // Refresh data
      await loadReportData();

      // Clear selections
      setSelectedServices(new Set());

      alert(
        `Pedidos atualizados com sucesso! ${selectedItems.length} itens processados.`,
      );
    } catch (error) {
      console.error("Error updating pedidos:", error);
      alert(
        "Erro ao atualizar pedidos. Verifique o console para mais detalhes.",
      );
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      "Planilhas",
      "Código",
      "Pedido",
      "Descrição do Serviço",
      "Itens do Contrato",
      "Unid",
      "Qtd",
      "Preço",
      "Fator",
      "Total",
      "PES",
      "Município",
    ].join(";");

    const csvData = filteredAndSortedData
      .map((item) =>
        [
          item.planilha,
          item.codigo,
          item.pedido,
          `"${item.descricaoServico}"`,
          `"${item.itensContrato}"`,
          item.unidade,
          item.quantidade.toString().padStart(3, "0"),
          item.preco.toFixed(2),
          item.fator.toFixed(4),
          item.total.toFixed(2),
          `"${item.si}"`,
          `"${item.municipio}"`,
        ].join(";"),
      )
      .join("\n");

    const csvContent = [headers, csvData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio-coelba-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <i className="fas fa-sort"></i>;
    return sortOrder === "asc" ? (
      <i className="fas fa-sort-up"></i>
    ) : (
      <i className="fas fa-sort-down"></i>
    );
  };

  return (
    <div className="coelba-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Coelba</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button
                  onClick={handleDeselectAllTeams}
                  className="control-btn"
                >
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar planilha, código, serviço, município..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {coelbaServiceData.length > 0 && (
              <>
                <button onClick={exportToCSV} className="export-btn">
                  <i className="fas fa-download"></i>
                  Exportar CSV
                </button>
                <button
                  onClick={handleUpdatePedidos}
                  className="update-pedidos-btn"
                  disabled={loading || selectedServices.size === 0}
                >
                  <i className="fas fa-edit"></i>
                  Atualizar Pedidos
                </button>
              </>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {coelbaServiceData.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalServices}</div>
                <div className="card-label">Total de Serviços</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.uniqueCodes}</div>
                <div className="card-label">Códigos Únicos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {reportSummary.uniqueMunicipios}
                </div>
                <div className="card-label">Municípios</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {reportSummary.totalQuantity.toFixed(0)}
                </div>
                <div className="card-label">Quantidade Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {formatCurrency(reportSummary.totalValue)}
                </div>
                <div className="card-label">Valor Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.selectedCount}</div>
                <div className="card-label">Itens Selecionados</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {coelbaServiceData.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({filteredAndSortedData.length} serviços)</h3>
              <div className="selection-controls">
                <button
                  onClick={handleSelectAllServices}
                  className="control-btn"
                >
                  Selecionar Todos
                </button>
                <button
                  onClick={handleDeselectAllServices}
                  className="control-btn"
                >
                  Desmarcar Todos
                </button>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="report-table coelba-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort("planilha")}
                      className="sortable-header"
                    >
                      Planilhas {getSortIcon("planilha")}
                    </th>
                    <th
                      onClick={() => handleSort("codigo")}
                      className="sortable-header"
                    >
                      Código {getSortIcon("codigo")}
                    </th>
                    <th>Pedido</th>
                    <th>Descrição do Serviço</th>
                    <th>Itens do Contrato</th>
                    <th>Unid</th>
                    <th>Qtd</th>
                    <th>Preço</th>
                    <th>Fator</th>
                    <th
                      onClick={() => handleSort("total")}
                      className="sortable-header"
                    >
                      Total {getSortIcon("total")}
                    </th>
                    <th>PES</th>
                    <th
                      onClick={() => handleSort("municipio")}
                      className="sortable-header"
                    >
                      Município {getSortIcon("municipio")}
                    </th>
                    <th>Selecione</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.map((item, index) => (
                    <tr
                      key={`${item.rowId}-${index}`}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td className="planilha-cell">
                        <strong
                          dangerouslySetInnerHTML={{ __html: item.planilha }}
                        ></strong>
                      </td>
                      <td className="code-cell">
                        <strong>{item.codigo}</strong>
                      </td>
                      <td className="pedido-cell">{item.pedido}</td>
                      <td
                        className="service-cell"
                        title={item.descricaoServico}
                      >
                        {item.descricaoServico}
                      </td>
                      <td className="contract-cell" title={item.itensContrato}>
                        {item.itensContrato}
                      </td>
                      <td className="unit-cell">{item.unidade}</td>
                      <td className="qty-cell">
                        {item.quantidade.toString().padStart(3, "0")}
                      </td>
                      <td className="price-cell">{item.preco.toFixed(2)}</td>
                      <td className="factor-cell">{item.fator.toFixed(4)}</td>
                      <td className="total-cell">
                        <strong>{item.total.toFixed(2)}</strong>
                      </td>
                      <td className="si-cell" title={item.si}>
                        {item.si}
                      </td>
                      <td className="city-cell" title={item.municipio}>
                        {item.municipio}
                      </td>
                      <td className="select-cell">
                        <input
                          type="checkbox"
                          checked={selectedServices.has(index.toString())}
                          onChange={() =>
                            handleServiceSelection(index.toString())
                          }
                          className="service-checkbox"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Total
                    </td>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      {formatCurrency(reportSummary.totalValue)}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Total Pagos
                    </td>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      {formatCurrency(reportSummary.totalValueWithPedido)}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Total sem pedidos
                    </td>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      {formatCurrency(reportSummary.totalValueWithoutPedido)}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {filteredAndSortedData.length === 0 &&
              coelbaServiceData.length > 0 && (
                <div className="empty-results">
                  <i className="fas fa-search"></i>
                  <p>Nenhum serviço encontrado com os filtros aplicados.</p>
                </div>
              )}
          </div>
        )}

        {coelbaServiceData.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-bolt"></i>
            <h3>Nenhum dado carregado</h3>
            <p>
              Configure os filtros e clique em "Gerar Relatório" para visualizar
              os dados da Coelba.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoelbaReport;
